import React, { useState, useEffect } from 'react';
import { Card, Collapse, Input, Button, Popconfirm, message, Empty, Spin } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { departmentBatchApi } from '../../services/api';

const photoTypeNames = {
  1: '原石照片', 3: '成品照片', 4: '检测证书', 5: '切料照片', 6: '规划照片', 7: '手修照片', 8: '抛光照片', 9: '原石入库照片', 10: '切料标块照片', 11: '打柱照片', 12: '磨珠照片', 13: '穿珠子照片'
};

const Presets = () => {
  const navigate = useCompanyNavigate();
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(false);
  const [newDescriptions, setNewDescriptions] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const deptAdmin = JSON.parse(localStorage.getItem('department_admin') || '{}');
  const allowedPhotoTypes = deptAdmin.photo_types ? deptAdmin.photo_types.split(',').map(Number) : [];

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const res = await departmentBatchApi.listPresets();
      setPresets(res.data || {});
      const init = {};
      allowedPhotoTypes.forEach(t => { init[t] = ''; });
      setNewDescriptions(init);
    } catch (err) {
      message.error('获取预设描述失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPresets(); }, []);

  const handleAdd = async (photoType) => {
    const text = newDescriptions[photoType]?.trim();
    if (!text) { message.warning('请输入描述文本'); return; }
    try {
      await departmentBatchApi.createPreset({ photoType, description: text });
      message.success('添加成功');
      setNewDescriptions(prev => ({ ...prev, [photoType]: '' }));
      fetchPresets();
    } catch (err) {
      message.error(err.response?.data?.message || '添加失败');
    }
  };

  const handleUpdate = async (id) => {
    if (!editText.trim()) { message.warning('描述不能为空'); return; }
    try {
      await departmentBatchApi.updatePreset(id, { description: editText.trim() });
      message.success('更新成功');
      setEditingId(null);
      fetchPresets();
    } catch (err) {
      message.error(err.response?.data?.message || '更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await departmentBatchApi.deletePreset(id);
      message.success('删除成功');
      fetchPresets();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  if (loading && Object.keys(presets).length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  const collapseItems = allowedPhotoTypes.map(type => ({
    key: String(type),
    label: photoTypeNames[type] || `类型${type}`,
    children: (
      <div>
        {(presets[type] || []).length === 0 ? (
          <Empty description="暂无预设描述" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(presets[type] || []).map(item => (
              <div key={item.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#fafafa',
                borderRadius: 6
              }}>
                {editingId === item.id ? (
                  <>
                    <Input
                      size="small"
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onPressEnter={() => handleUpdate(item.id)}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<CheckOutlined />}
                      style={{ color: '#52c41a' }}
                      onClick={() => handleUpdate(item.id)}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => setEditingId(null)}
                    />
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 14 }}>{item.description}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => { setEditingId(item.id); setEditText(item.description); }}
                    />
                    <Popconfirm title="确定删除?" onConfirm={() => handleDelete(item.id)}>
                      <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Input
            size="small"
            placeholder="输入新的预设描述..."
            value={newDescriptions[type] || ''}
            onChange={e => setNewDescriptions(prev => ({ ...prev, [type]: e.target.value }))}
            onPressEnter={() => handleAdd(type)}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAdd(type)}
            style={{ background: '#8B7355', borderColor: '#8B7355' }}
          >
            添加
          </Button>
        </div>
      </div>
    )
  }));

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/department')}
          style={{ marginRight: 8 }}
        />
        <span style={{ fontSize: 16, fontWeight: 600 }}>预设描述管理</span>
      </div>

      <Card size="small" style={{ borderRadius: 8 }}>
        <Collapse
          defaultActiveKey={allowedPhotoTypes.map(String)}
          ghost
          items={collapseItems}
          style={{ background: 'transparent' }}
        />
      </Card>
    </div>
  );
};

export default Presets;
